/**
 * Générateur — Attendu 657 — Résoudre des problèmes mettant en jeu des
 * distances à un point.
 * Niveau : 6ème | Cluster : 30 | Domaine : Espace et géométrie
 *
 * 5 exercices / 5 formats :
 *   - zone_atteignable_qcm_brouteur : QCM 4 choix (chèvre/chien brouteur), 3
 *                                      régimes (PM < L, PM = L, PM > L), pas
 *                                      de figure
 *   - zone_figure_vf_brouteur       : V/F (3V+3F stricte) sur figure SVG
 *                                      (poteau + 4–6 objets + distances
 *                                      étiquetées en mètres)
 *   - distance_max_saisie_brouteur  : saisie numérique (3 sous-régimes : L,
 *                                      d+L, PM−L) avec suffixe " m"
 *   - tresor_deux_cercles_programme : programme_construction 2 tâches
 *                                      (2 cercles sécants), contextes trésor
 *   - echelle_saisie_conversion     : saisie numérique, 3 sous-régimes
 *                                      (carte→réel, réel→carte, laisse/carte)
 *
 * Variété cible : ≥ 20 variantes uniques par format (contrainte auditGen).
 * Voir docs/fiches/FICHE_657.md et docs/fiches/PAYLOADS_657.md.
 */

import {
    randInt, randomChoice, randomChoiceMultiple, shuffle, pickLettresSains,
    makeQcm, makeSaisie, makeVraiFaux, makeProgrammeConstruction,
} from '../../../utils';

// ═══════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 657;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_ZONE_QCM = 'Zone atteignable (QCM brouteur)';
const L_ZONE_FIGURE = 'Zone atteignable (V/F sur figure)';
const L_DISTANCE_MAX = 'Distance maximale parcourue';
const L_TRESOR = 'Trésor (construction 2 cercles)';
const L_ECHELLE = 'Problème d\'échelle (carte ↔ réel)';

// Lettres valides pour nommer les points (exclut I et O : ambiguïté avec 1 et 0).
// Identique au pool de gen_655 et gen_656 (cohérence cluster 30).
const LETTRES = 'ABCDEFGHJKLMNPQRSTUVWYZ'.split('');

// Constantes SVG (programme_construction — alignées avec gen_655 / gen_656)
const PX_PER_CM = 30;
const VIEWBOX_PC = { w: 600, h: 400 };
const TOLERANCE_PC = 12;

// Couleurs pour les tâches programme_construction
const COLOR_BLEU = '#378ADD';
const COLOR_ROUGE = '#E24B4A';
const COLOR_VERT = '#1D9E75';

// ═══════════════════════════════════════════════════════════
// HELPERS COMMUNS
// ═══════════════════════════════════════════════════════════

/** Encode une chaîne SVG en base64, compatible navigateur + Node. */
const svgToBase64 = (svg) => {
    if (typeof btoa === 'function') {
        try { return btoa(unescape(encodeURIComponent(svg))); } catch (_e) { /* fallthrough */ }
    }
    return Buffer.from(svg, 'utf-8').toString('base64');
};

/** Formate un nombre à la française (virgule décimale). */
const fmt = (n) => {
    if (Number.isInteger(n)) return String(n);
    return String(n).replace('.', ',');
};

/** Tire n lettres distinctes dans un pool (par défaut LETTRES). */
const pickN = (n, pool = LETTRES) => pickLettresSains(pool, n);

/** Distance euclidienne entre deux points {x,y}. */
const dist = (p, q) => Math.hypot(p.x - q.x, p.y - q.y);

/** Tente de placer un point à une distance dans [dMin, dMax] du centre. */
const pickPointAtDistance = (cx, cy, dMin, dMax, bounds, maxAttempts = 80) => {
    for (let i = 0; i < maxAttempts; i++) {
        const d = dMin + Math.random() * (dMax - dMin);
        const angle = Math.random() * 2 * Math.PI;
        const x = cx + d * Math.cos(angle);
        const y = cy + d * Math.sin(angle);
        if (x >= bounds.minX && x <= bounds.maxX && y >= bounds.minY && y <= bounds.maxY) {
            return { x, y, d };
        }
    }
    return null;
};

// ═══════════════════════════════════════════════════════════
// CONTEXTES NARRATIFS
// ═══════════════════════════════════════════════════════════

// Ex. 1 / Ex. 2 / Ex. 3 — contextes animal + support + objet
const CONTEXTES_ANIMAL = [
    { animal: 'chien',  article: 'le',  possessif: 'son', pronom: 'il',   support: 'piquet',  objets: ['os', 'gamelle', 'niche', 'bol d\'eau', 'jouet'] },
    { animal: 'chèvre', article: 'la',  possessif: 'sa',  pronom: 'elle', support: 'arbre',   objets: ['seau', 'bac de nourriture', 'abreuvoir', 'tas de foin'] },
    { animal: 'chat',   article: 'le',  possessif: 'son', pronom: 'il',   support: 'anneau',  objets: ['souris en tissu', 'bol de lait', 'panier', 'balle'] },
    { animal: 'lapin',  article: 'le',  possessif: 'son', pronom: 'il',   support: 'poteau',  objets: ['carotte', 'tas de foin', 'abri', 'gamelle'] },
];

// Ex. 4 — contextes trésor (A, B)
//
// Le GENRE de chaque nom est déclaré, jamais deviné à l'exécution. L'ancien code
// codait « du » en dur devant A_objet, ne testait que la voyelle initiale pour
// B_objet, et supposait que tout nom autre que « trésor » était féminin — d'où
// « du cabane », « du fontaine », « Un cache est caché », « pour cette repère ».
// Mesuré sur 5000 tirages : la moitié des énoncés portait au moins une faute.
const CONTEXTES_TRESOR = [
    { theme: 'pirate',      A_objet: 'puits',   A_genre: 'm', B_objet: 'arbre tordu', B_genre: 'm', noun_trésor: 'trésor',  T_genre: 'm' },
    { theme: 'scout',       A_objet: 'rocher',  A_genre: 'm', B_objet: 'fontaine',    B_genre: 'f', noun_trésor: 'cache',   T_genre: 'f' },
    { theme: 'randonneur',  A_objet: 'cabane',  A_genre: 'f', B_objet: 'pont',        B_genre: 'm', noun_trésor: 'repère',  T_genre: 'm' },
    { theme: 'archéologue', A_objet: 'colonne', A_genre: 'f', B_objet: 'temple',      B_genre: 'm', noun_trésor: 'vestige', T_genre: 'm' },
];

/**
 * « du puits » / « de la cabane » / « de l'arbre tordu ».
 * L'élision prime sur le genre : on dit « de l'ancre », pas « de la ancre ».
 */
const deLObjet = (nom, genre) =>
    (debuteParVoyelle(nom) ? `de l'${nom}` : (genre === 'f' ? `de la ${nom}` : `du ${nom}`));

// ═══════════════════════════════════════════════════════════
// Ex. 1 — Zone atteignable (QCM brouteur, sans figure)
// ═══════════════════════════════════════════════════════════

/** Pool fixe des 4 options QCM Ex. 1, instanciées avec les variables. */
// Possessif accordé à l'OBJET (cherry-pick Yannis 2026-07-28) : l'ancien code
// passait ctx.possessif — accordé à l'ANIMAL — d'où « la chèvre atteint sa
// seau », « le lapin et son carotte ». La liste couvre tous les objets
// féminins du pool, « niche » compris (oubli du patch d'origine).
const OBJ_FEMININ = new Set(['gamelle', 'souris', 'balle', 'carotte', 'niche']);
const possessifObjet = (noun) => {
    const premierMot = String(noun).split(' ')[0];
    if (/^[aeiouyéèêh]/.test(premierMot)) return 'son'; // élision : « son abreuvoir »
    return OBJ_FEMININ.has(premierMot) ? 'sa' : 'son';
};

/**
 * Articles du SUPPORT, avec élision. Le pool contient `arbre` et `anneau` :
 * sans ces helpers on produisait « du arbre », « le anneau », « au arbre » —
 * mesuré sur 3000 tirages, la moitié des énoncés des Ex. 1 et 2 étaient
 * touchés, un tiers de ceux de l'Ex. 3.
 */
const debuteParVoyelle = (mot) => /^[aeiouyéèêh]/i.test(String(mot));
/** « le piquet » / « l'arbre » */
const leSupport = (s) => (debuteParVoyelle(s) ? `l'${s}` : `le ${s}`);
/** « du piquet » / « de l'arbre » */
const duSupport = (s) => (debuteParVoyelle(s) ? `de l'${s}` : `du ${s}`);
/** « au piquet » / « à l'arbre » */
const auSupport = (s) => (debuteParVoyelle(s) ? `à l'${s}` : `au ${s}`);
/** « attaché » / « attachée » — seule la chèvre est féminine dans le pool. */
const attache = (ctx) => `attaché${ctx.article === 'la' ? 'e' : ''}`;
/** Article de l'animal, capitalisé en début de phrase. */
const animalCap = (ctx) => ctx.article.charAt(0).toUpperCase() + ctx.article.slice(1);

const buildChoixEx1 = (article, animal, possessif, objetArticleDefini, objetNoun, M) => ({
    // A vrai UNIQUEMENT si PM < L (cherry-pick Yannis) : « avec de la marge »
    // casse l'implication A ⊂ B — au régime PM = L, seul B est vrai.
    A: `Oui, ${article} ${animal} atteint ${possessif} ${objetNoun} ${M} avec de la marge (la corde n'est pas tendue).`,
    B: `Oui, ${article} ${animal} atteint ${possessif} ${objetNoun} ${M} exactement à l'extrémité de sa corde.`,
    C: `Non, ${article} ${animal} n'atteint pas ${possessif} ${objetNoun} ${M}.`,
    D: `On ne peut pas conclure sans tracer la figure à l'échelle.`,
});

const genZoneAtteignableQcmBrouteur = () => {
    const ctx = randomChoice(CONTEXTES_ANIMAL);
    const objetNoun = randomChoice(ctx.objets);
    const [P, M] = pickN(2);
    const L = randomChoice([3, 4, 5, 6, 7, 8, 10]);
    const regime = randomChoice(['a', 'b', 'c']); // PM<L / PM=L / PM>L

    let PM;
    let bonneKey;
    if (regime === 'a') {
        PM = randInt(1, L - 1);
        bonneKey = 'A';
    } else if (regime === 'b') {
        PM = L;
        bonneKey = 'B';
    } else {
        PM = L + randInt(1, 5);
        bonneKey = 'C';
    }

    // Possessif accordé à l'OBJET (pas à l'animal) — cherry-pick Yannis.
    const possObjet = possessifObjet(objetNoun);
    const choixMap = buildChoixEx1(ctx.article, ctx.animal, possObjet, '', objetNoun, M);
    const choix = [choixMap.A, choixMap.B, choixMap.C, choixMap.D];
    const bonne = choixMap[bonneKey];

    const texte =
        `<p>${animalCap(ctx)} ${ctx.animal} est ${attache(ctx)} à un ${ctx.support} ${P} par une corde de ${L} m.</p>`
        + `<p>${possObjet.charAt(0).toUpperCase() + possObjet.slice(1)} ${objetNoun} ${M} est à ${P}${M} = ${PM} m ${duSupport(ctx.support)}.</p>`
        + `<p>${animalCap(ctx)} ${ctx.animal} peut-${ctx.pronom} atteindre ${possObjet} ${objetNoun} ?</p>`;

    const payload = makeQcm(choix, bonne, texte, opts(L_ZONE_QCM));
    payload._meta.generatorKey = 'zone_atteignable_qcm_brouteur';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 2 — Zone atteignable sur figure (V/F + image SVG)
// ═══════════════════════════════════════════════════════════

const EX2_VIEWBOX = { w: 300, h: 220 };
const EX2_CENTER = { x: 150, y: 110 };
const EX2_PX_PER_M = 15;
const EX2_MARGIN = 18;

/**
 * Construit le SVG figure Ex. 2 : poteau central + N objets avec segments
 * pointillés + étiquettes de distance en mètres.
 */
const buildFigureEx2Svg = (P_name, objets) => {
    const { x: Px, y: Py } = EX2_CENTER;
    // 1. Segments pointillés P → chaque objet
    const segments = objets.map(o =>
        `<line x1="${Px}" y1="${Py}" x2="${o.x.toFixed(1)}" y2="${o.y.toFixed(1)}" `
        + `stroke="#666" stroke-dasharray="4 3" stroke-width="1" />`
    ).join('');
    // 2. Poteau (disque marron)
    const poteau =
        `<circle cx="${Px}" cy="${Py}" r="4" fill="#8B4513" />`
        + `<text x="${Px - 14}" y="${Py + 4}" font-size="14" font-weight="bold" fill="#000">${P_name}</text>`;
    // 3. Objets + labels nom (les mesures de distance sont données dans l'énoncé, pas sur la figure)
    const objs = objets.map(o => {
        // Position label nom : décalé radialement de 11 px depuis le poteau
        const vx = o.x - Px;
        const vy = o.y - Py;
        const L = Math.hypot(vx, vy) || 1;
        const nameLx = (o.x + (vx / L) * 11).toFixed(1);
        const nameLy = (o.y + (vy / L) * 11 + 4).toFixed(1);
        return (
            `<circle cx="${o.x.toFixed(1)}" cy="${o.y.toFixed(1)}" r="3" fill="#000" />`
            + `<text x="${nameLx}" y="${nameLy}" font-size="13" fill="#000">${o.name}</text>`
        );
    }).join('');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${EX2_VIEWBOX.w} ${EX2_VIEWBOX.h}">${segments}${poteau}${objs}</svg>`;
};

/**
 * Place N objets avec distances au poteau :
 * - au moins 2 objets avec distM < L (atteignables)
 * - au moins 2 objets avec distM > L (hors portée)
 * - aucun à distM = L (cas limite évité en V/F)
 */
const placerObjetsEx2 = (names, L) => {
    const { x: Px, y: Py } = EX2_CENTER;
    const bounds = {
        minX: EX2_MARGIN, maxX: EX2_VIEWBOX.w - EX2_MARGIN,
        minY: EX2_MARGIN, maxY: EX2_VIEWBOX.h - EX2_MARGIN,
    };
    const N = names.length;
    const nIn = randInt(2, Math.min(3, N - 2)); // 2 ou 3 atteignables
    const nOut = N - nIn;

    const maxTries = 80;
    for (let attempt = 0; attempt < maxTries; attempt++) {
        const placed = [];
        let ok = true;
        // Places les "in" : distM ∈ [1, L−1]
        for (let k = 0; k < nIn && ok; k++) {
            const distM = randInt(1, Math.max(1, L - 1));
            const distPx = distM * EX2_PX_PER_M;
            const pt = pickPointAtDistance(Px, Py, distPx, distPx, bounds);
            if (!pt) { ok = false; break; }
            if (placed.some(q => dist(pt, q) < 30)) { ok = false; break; }
            placed.push({ ...pt, distM });
        }
        if (!ok) continue;
        // Place les "out" : distM ∈ [L+1, L+5] (plafonné par viewBox)
        const maxDistM = Math.min(L + 5, Math.floor(Math.min(EX2_VIEWBOX.w, EX2_VIEWBOX.h) / (2 * EX2_PX_PER_M)) - 1);
        for (let k = 0; k < nOut && ok; k++) {
            const distM = randInt(L + 1, Math.max(L + 1, maxDistM));
            const distPx = distM * EX2_PX_PER_M;
            const pt = pickPointAtDistance(Px, Py, distPx, distPx, bounds);
            if (!pt) { ok = false; break; }
            if (placed.some(q => dist(pt, q) < 30)) { ok = false; break; }
            placed.push({ ...pt, distM });
        }
        if (!ok) continue;
        // Shuffle pour mélanger l'ordre visuel des in/out
        const shuffled = shuffle(placed);
        return shuffled.map((p, i) => ({ ...p, name: names[i] }));
    }
    // Fallback déterministe (config fixe)
    const fallback = [];
    for (let i = 0; i < N; i++) {
        const distM = i < nIn ? Math.max(1, L - 1 - i) : L + 1 + (i - nIn);
        const angle = (i / N) * 2 * Math.PI;
        fallback.push({
            name: names[i],
            x: Px + distM * EX2_PX_PER_M * Math.cos(angle),
            y: Py + distM * EX2_PX_PER_M * Math.sin(angle),
            distM,
        });
    }
    return fallback;
};

/** Construit les 6 modèles d'affirmations (VRAIES) instanciées par paire (objet). */
const buildPoolVraiesEx2 = (animal, article, objets) => {
    const pool = [];
    for (const o of objets) {
        if (o.atteint) {
            pool.push({
                text: `${article.charAt(0).toUpperCase() + article.slice(1)} ${animal} peut atteindre ${o.name}.`,
                kind: 'atteint',
            });
            pool.push({
                text: `${o.name} se trouve à l'intérieur de la zone atteignable.`,
                kind: 'in_zone',
            });
        } else {
            pool.push({
                text: `${o.name} est hors de portée ${article === 'la' ? 'de la' : 'du'} ${animal}.`,
                kind: 'hors_portee',
            });
            pool.push({
                text: `${article.charAt(0).toUpperCase() + article.slice(1)} ${animal} ne peut pas atteindre ${o.name}.`,
                kind: 'ne_peut_pas',
            });
            pool.push({
                text: `${o.name} se trouve hors de la zone atteignable.`,
                kind: 'hors_zone',
            });
        }
    }
    // Comparaisons : {X} plus proche de {P} que {Y}
    for (let i = 0; i < objets.length; i++) {
        for (let j = 0; j < objets.length; j++) {
            if (i === j) continue;
            if (objets[i].distM < objets[j].distM) {
                pool.push({
                    text: `${objets[i].name} est plus proche du poteau que ${objets[j].name}.`,
                    kind: 'plus_proche',
                });
            }
        }
    }
    return pool;
};

/** Construit les 6 modèles d'affirmations (FAUSSES) instanciées par paire (objet). */
const buildPoolFaussesEx2 = (animal, article, objets) => {
    const pool = [];
    for (const o of objets) {
        if (o.atteint) {
            // Faux : "hors de portée" alors qu'il est atteint
            pool.push({
                text: `${o.name} est hors de portée ${article === 'la' ? 'de la' : 'du'} ${animal}.`,
                kind: 'hors_portee_faux',
            });
            pool.push({
                text: `${article.charAt(0).toUpperCase() + article.slice(1)} ${animal} ne peut pas atteindre ${o.name}.`,
                kind: 'ne_peut_pas_faux',
            });
            pool.push({
                text: `${o.name} se trouve hors de la zone atteignable.`,
                kind: 'hors_zone_faux',
            });
        } else {
            pool.push({
                text: `${article.charAt(0).toUpperCase() + article.slice(1)} ${animal} peut atteindre ${o.name}.`,
                kind: 'atteint_faux',
            });
            pool.push({
                text: `${o.name} se trouve à l'intérieur de la zone atteignable.`,
                kind: 'in_zone_faux',
            });
        }
    }
    // Comparaisons fausses : {X} plus proche que {Y} alors que distX ≥ distY
    for (let i = 0; i < objets.length; i++) {
        for (let j = 0; j < objets.length; j++) {
            if (i === j) continue;
            if (objets[i].distM >= objets[j].distM) {
                pool.push({
                    text: `${objets[i].name} est plus proche du poteau que ${objets[j].name}.`,
                    kind: 'plus_proche_faux',
                });
            }
        }
    }
    return pool;
};

const genZoneFigureVfBrouteur = () => {
    const ctx = randomChoice(CONTEXTES_ANIMAL);
    const N = randomChoice([4, 5, 6]);
    const L = randomChoice([3, 4, 5, 6, 7, 8]);
    // Tirage des noms : 1 pour le poteau + N pour les objets
    const picked = pickN(1 + N);
    const P_name = picked[0];
    const objetNames = picked.slice(1);

    const objets = placerObjetsEx2(objetNames, L).map(o => ({
        ...o,
        atteint: o.distM < L,
    }));

    const svg = buildFigureEx2Svg(P_name, objets);
    const imageUrl = `data:image/svg+xml;base64,${svgToBase64(svg)}`;

    const poolVraies = buildPoolVraiesEx2(ctx.animal, ctx.article, objets);
    const poolFausses = buildPoolFaussesEx2(ctx.animal, ctx.article, objets);

    // Tirage 3V + 3F distincts (par texte)
    const uniqVraies = [...new Map(poolVraies.map(x => [x.text, x])).values()];
    const uniqFausses = [...new Map(poolFausses.map(x => [x.text, x])).values()];
    const chosenVraies = randomChoiceMultiple(uniqVraies, 3).map(x => x.text);
    const chosenFausses = randomChoiceMultiple(uniqFausses, 3).map(x => x.text);

    // SHUFFLE côté générateur — OBLIGATOIRE
    const merged = shuffle([
        ...chosenVraies.map(l => ({ ligne: l, reponse: 0 })),
        ...chosenFausses.map(l => ({ ligne: l, reponse: 1 })),
    ]);
    const lignes = merged.map(x => x.ligne);
    const bonnesReponses = merged.map(x => x.reponse);

    // Liste des distances données dans l'énoncé (format : BF = 5 m ; BE = 3 m ; ...)
    const distancesStr = objets.map(o => `${P_name}${o.name} = ${o.distM} m`).join(' ; ');
    const texte =
        `<p>${animalCap(ctx)} ${ctx.animal} est ${attache(ctx)} ${auSupport(ctx.support)} ${P_name} par une corde de ${L} m.</p>`
        + `<p>Distances des objets ${auSupport(ctx.support)} : ${distancesStr}.</p>`;

    const payload = makeVraiFaux(lignes, bonnesReponses, texte, {
        ...opts(L_ZONE_FIGURE),
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'image', config: { imageUrl } }],
        },
    });
    payload._meta.generatorKey = 'zone_figure_vf_brouteur';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 3 — Distance maximale (saisie numérique, 3 sous-régimes)
// ═══════════════════════════════════════════════════════════

// Mini-schéma SVG pour distance_max_saisie_brouteur.
// Régimes : 'a' = corde seule (cercle autour de P), 'b' = mur hachuré + cercle,
// 'c' = P + cercle + point M hors de portée.
const DMS_VIEWBOX = { w: 280, h: 180 };
const DMS_PX_PER_M = 8;

const buildDistanceMaxSchemaSvg = (regime, { L, d, PM, P, M }) => {
    const { w: W, h: H } = DMS_VIEWBOX;
    const cy = H / 2;
    const R = L * DMS_PX_PER_M;
    const defs = `<defs><pattern id="dms-hatch" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)"><line x1="0" y1="0" x2="0" y2="6" stroke="#7a7a7a" stroke-width="1.5"/></pattern></defs>`;

    let Px;
    const parts = [];

    if (regime === 'b') {
        const wallW = 10;
        const dPx = d * DMS_PX_PER_M;
        // Le mur est repoussé vers la droite juste ce qu'il faut pour que le
        // cercle de rayon R centré en P tienne dans le cadre. Avec wallX = 18
        // fixe, un rayon de 80 px et un mur à 1 m sortaient de 44 px à gauche.
        const wallX = Math.max(18, R - dPx - wallW + 4);
        Px = wallX + wallW + dPx;
        // Mur hachuré + arête nette côté intérieur
        parts.push(`<rect x="${wallX}" y="22" width="${wallW}" height="${H - 44}" fill="url(#dms-hatch)" stroke="#333" stroke-width="1.2"/>`);
        parts.push(`<line x1="${wallX + wallW}" y1="22" x2="${wallX + wallW}" y2="${H - 22}" stroke="#333" stroke-width="2"/>`);
        parts.push(`<text x="${wallX + wallW / 2}" y="18" text-anchor="middle" font-size="11" fill="#555">mur</text>`);
        // Distance mur ↔ P (label placé SOUS la ligne pour ne pas chevaucher l'étiquette de P)
        parts.push(`<line x1="${wallX + wallW}" y1="${cy}" x2="${Px}" y2="${cy}" stroke="#666" stroke-width="1" stroke-dasharray="3 2"/>`);
        parts.push(`<text x="${(wallX + wallW + Px) / 2}" y="${cy + 14}" text-anchor="middle" font-size="11" fill="#333">${d} m</text>`);
    } else if (regime === 'a') {
        Px = W / 2;
    } else {
        // Px calé sur le RAYON, pas sur une constante : avec Px = 38 fixe et
        // R = L × 8 (L jusqu'à 10, donc R = 80), le cercle sortait de 42 px à
        // gauche — précisément du côté où l'élève lit la portée de la corde.
        Px = R + 6;
    }

    // Cercle atteignable (pointillés bleus) + rayon annoté
    parts.push(`<circle cx="${Px}" cy="${cy}" r="${R}" fill="none" stroke="#378ADD" stroke-width="1.5" stroke-dasharray="4 3"/>`);
    parts.push(`<line x1="${Px}" y1="${cy}" x2="${Px + R}" y2="${cy}" stroke="#378ADD" stroke-width="1.2"/>`);
    parts.push(`<text x="${Px + R / 2}" y="${cy + 13}" text-anchor="middle" font-size="11" fill="#1E5BA6" font-weight="600">${L} m</text>`);
    // P : disque + étiquette (placée au-dessus, centrée sur le point, pour rester proche)
    parts.push(`<circle cx="${Px}" cy="${cy}" r="3.2" fill="#222"/>`);
    parts.push(`<text x="${Px}" y="${cy - 8}" text-anchor="middle" font-size="12" fill="#222" font-weight="600">${P}</text>`);

    if (regime === 'c') {
        const Mx = Px + PM * DMS_PX_PER_M;
        parts.push(`<circle cx="${Mx}" cy="${cy}" r="3.2" fill="#222"/>`);
        parts.push(`<text x="${Mx + 6}" y="${cy - 6}" font-size="12" fill="#222" font-weight="600">${M}</text>`);
        const yBelow = cy + 28;
        parts.push(`<line x1="${Px}" y1="${yBelow}" x2="${Mx}" y2="${yBelow}" stroke="#666" stroke-width="1" stroke-dasharray="3 2"/>`);
        parts.push(`<text x="${(Px + Mx) / 2}" y="${yBelow + 12}" text-anchor="middle" font-size="11" fill="#333">${P}${M} = ${PM} m</text>`);
    }

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}">${defs}${parts.join('')}</svg>`;
};

const genDistanceMaxSaisieBrouteur = () => {
    const ctx = randomChoice(CONTEXTES_ANIMAL);
    const objetNoun = randomChoice(ctx.objets);
    const [P, M] = pickN(2);
    const L = randInt(3, 10);
    const sousRegime = randomChoice(['a', 'b', 'c']);

    let bonneReponse;
    let schemaArgs;
    let texte;
    // Possessif accordé à l'OBJET, pas à l'animal : `ctx.possessif` produisait
    // « sa seau » pour la chèvre et « son gamelle » pour le chien (59 % du
    // régime c mesurés). Le helper existait déjà, il n'était utilisé qu'à l'Ex. 1.
    const possObjet = possessifObjet(objetNoun);
    const possObjetCap = possObjet.charAt(0).toUpperCase() + possObjet.slice(1);
    const intro = `<p>${animalCap(ctx)} ${ctx.animal} est ${attache(ctx)} à un ${ctx.support} ${P} par une corde de ${L} m.</p>`;

    if (sousRegime === 'a') {
        bonneReponse = L;
        schemaArgs = { L, P };
        texte = intro
            + `{{element:0}}`
            + `<p>Quelle distance maximale peut-${ctx.pronom} atteindre depuis ${P} ?</p>`;
    } else if (sousRegime === 'b') {
        const d = randInt(1, 5);
        bonneReponse = d + L;
        schemaArgs = { L, d, P };
        texte = intro
            + `<p>${leSupport(ctx.support).charAt(0).toUpperCase() + leSupport(ctx.support).slice(1)} ${P} est à ${d} m d'un mur.</p>`
            + `{{element:0}}`
            + `<p>En s'éloignant du mur, à quelle distance maximale du mur peut-${ctx.pronom} aller ?</p>`;
    } else {
        const PM = L + randInt(1, 6);
        bonneReponse = PM - L;
        schemaArgs = { L, PM, P, M };
        texte = intro
            + `<p>${possObjetCap} ${objetNoun} ${M} est à ${P}${M} = ${PM} m ${duSupport(ctx.support)}.</p>`
            + `{{element:0}}`
            + `<p>Combien de mètres lui manque-t-il pour l'atteindre ?</p>`;
    }

    const schemaSvg = buildDistanceMaxSchemaSvg(sousRegime, schemaArgs);
    const imageUrl = `data:image/svg+xml;base64,${svgToBase64(schemaSvg)}`;

    const payload = makeSaisie(String(bonneReponse), texte, {
        ...opts(L_DISTANCE_MAX),
        suffixe: ' m',
        elements_additionnels: {
            version: 1,
            elements: [{ type: 'image', config: { imageUrl } }],
        },
    });
    payload._meta.generatorKey = 'distance_max_saisie_brouteur';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 4 — Trésor 2 cercles sécants (programme_construction)
// ═══════════════════════════════════════════════════════════

/** Tire une configuration valide (A, B, d1, d2) telle que les cercles sont sécants. */
const tirerConfigEx4Tresor = () => {
    const maxTries = 200;
    const margin = 60;
    const W = VIEWBOX_PC.w;
    const H = VIEWBOX_PC.h;
    const bboxMargin = 10;

    for (let i = 0; i < maxTries; i++) {
        const d1Cm = randomChoice([2, 3, 4, 5]);
        const d2Cm = randomChoice([2, 3, 4, 5]);
        const R1 = d1Cm * PX_PER_CM;
        const R2 = d2Cm * PX_PER_CM;

        // Centres placés dans le viewBox avec marge
        const Ax = randInt(margin, W - margin);
        const Ay = randInt(margin, H - margin);
        const Bx = randInt(margin, W - margin);
        const By = randInt(margin, H - margin);

        const d = Math.hypot(Ax - Bx, Ay - By);
        // Sécance stricte avec marge ±8 px
        if (d < Math.abs(R1 - R2) + 8) continue;
        if (d > R1 + R2 - 8) continue;

        // Les 2 cercles doivent rester dans le viewBox
        if (Ax - R1 < bboxMargin || Ax + R1 > W - bboxMargin) continue;
        if (Ay - R1 < bboxMargin || Ay + R1 > H - bboxMargin) continue;
        if (Bx - R2 < bboxMargin || Bx + R2 > W - bboxMargin) continue;
        if (By - R2 < bboxMargin || By + R2 > H - bboxMargin) continue;

        // Distance minimale entre A et B pour éviter chevauchement de labels
        if (d < 55) continue;

        return { Ax, Ay, Bx, By, d1Cm, d2Cm, R1, R2 };
    }
    // Fallback déterministe (config fixe garantie sécante et dans viewBox)
    return { Ax: 200, Ay: 200, Bx: 420, By: 200, d1Cm: 3, d2Cm: 4, R1: 90, R2: 120 };
};

const genTresorDeuxCerclesProgramme = () => {
    const ctxTresor = randomChoice(CONTEXTES_TRESOR);
    const [A_name, B_name] = pickN(2);
    const cfg = tirerConfigEx4Tresor();

    const points = [
        { name: A_name, x: cfg.Ax, y: cfg.Ay },
        { name: B_name, x: cfg.Bx, y: cfg.By },
    ];

    // Tâches en MÈTRES (bilan prof D6, 2026-07-27) : l'énoncé parle en m, la
    // figure travaille en carreaux (unite: 'm' → mention « 1 carreau = 1 m »).
    // Plus aucune conversion nulle part — l'ancien mélange « énoncé 4 m /
    // tâche 4 cm » sans phrase d'échelle est supprimé.
    // Les deux intersections des cercles : c'est là que le trésor peut être.
    // Tracer les deux cercles ne suffit pas à répondre — sans ces deux tâches,
    // l'élève laisse la conclusion implicite.
    const dAB = Math.hypot(cfg.Bx - cfg.Ax, cfg.By - cfg.Ay);
    const aProj = (dAB * dAB - cfg.R2 * cfg.R2 + cfg.R1 * cfg.R1) / (2 * dAB);
    const hInter = Math.sqrt(Math.max(0, cfg.R1 * cfg.R1 - aProj * aProj));
    const baseX = cfg.Ax + (aProj * (cfg.Bx - cfg.Ax)) / dAB;
    const baseY = cfg.Ay + (aProj * (cfg.By - cfg.Ay)) / dAB;
    const perpX = -(cfg.By - cfg.Ay) / dAB;
    const perpY = (cfg.Bx - cfg.Ax) / dAB;
    const inter1 = { x: Math.round(baseX + hInter * perpX), y: Math.round(baseY + hInter * perpY) };
    const inter2 = { x: Math.round(baseX - hInter * perpX), y: Math.round(baseY - hInter * perpY) };

    const tasks = [
        {
            id: 'a',
            consigne: `Trace en <b>bleu</b> l'ensemble des points situés à ${cfg.d1Cm} m de ${A_name}.`,
            color: COLOR_BLEU,
            shape: { type: 'circle', cx: cfg.Ax, cy: cfg.Ay, r: cfg.R1 },
        },
        {
            id: 'b',
            consigne: `Trace en <b>rouge</b> l'ensemble des points situés à ${cfg.d2Cm} m de ${B_name}.`,
            color: COLOR_ROUGE,
            shape: { type: 'circle', cx: cfg.Bx, cy: cfg.By, r: cfg.R2 },
        },
        {
            // Tâche `segment` : le composant attend DEUX clics, chacun accroché
            // à une extrémité attendue, dans n'importe quel ordre. C'est le seul
            // mode qui fait toucher deux points en une tâche — deux tâches
            // séparées auraient obligé à désigner laquelle est laquelle.
            id: 'c',
            kind: 'segment',
            // `pointsOnly` : le mode segment sert ici uniquement à faire cliquer
            // les DEUX points. Sans lui, le composant trace le trait qui les
            // joint, et l'élève pourrait croire que le trésor est n'importe où
            // le long de ce trait alors qu'il n'y a que deux positions.
            pointsOnly: true,
            consigne: `Touche en <b>vert</b> les <b>deux points d'intersection</b> des cercles : ce sont les positions possibles.`,
            color: COLOR_VERT,
            shape: { type: 'segment', p1: inter1, p2: inter2 },
        },
    ];

    // Tous les accords dérivent des genres déclarés dans CONTEXTES_TRESOR.
    const fem = ctxTresor.T_genre === 'f';
    const texte =
        `<p>${fem ? 'Une' : 'Un'} ${ctxTresor.noun_trésor} est caché${fem ? 'e' : ''} quelque part. `
        + `${fem ? 'Elle' : 'Il'} est à <b>${cfg.d1Cm} m</b> ${deLObjet(ctxTresor.A_objet, ctxTresor.A_genre)} ${A_name} `
        + `et à <b>${cfg.d2Cm} m</b> ${deLObjet(ctxTresor.B_objet, ctxTresor.B_genre)} ${B_name}.</p>`
        + `<p>Trace les deux cercles, puis place les positions possibles pour ${fem ? 'cette' : 'ce'} ${ctxTresor.noun_trésor}.</p>`;

    const payload = makeProgrammeConstruction(
        {
            pxPerCm: PX_PER_CM,
            tolerance: TOLERANCE_PC,
            viewBox: VIEWBOX_PC,
            points,
            preShapes: [],
            tasks,
            unite: 'm',
        },
        texte,
        opts(L_TRESOR),
    );
    payload._meta.generatorKey = 'tresor_deux_cercles_programme';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Ex. 5 — Problème d'échelle (saisie numérique, 3 sous-régimes)
// ═══════════════════════════════════════════════════════════

// `1` retiré : à l'échelle « 1 cm pour 1 m », la conversion est l'identité et
// l'élève recopie le nombre de l'énoncé. 14,8 % des tirages ne demandaient
// aucun calcul. L'espace passe de 616 à 528 variantes, toujours très au-dessus
// de la cible de 20.
const ECHELLE_K_VALUES = [2, 5, 10, 20, 50, 100];
const ECHELLE_Q_VALUES = [1, 2, 3, 4, 5, 6, 8, 10];
const ECHELLE_CONTEXTES_CARTE = [
    { entite: 'villages', noun: 'villages' },
    { entite: 'villes', noun: 'villes' },
    { entite: 'refuges', noun: 'refuges' },
    { entite: 'points de contrôle', noun: 'points de contrôle' },
];

const genEchelleSaisieConversion = () => {
    const sousRegime = randomChoice(['a', 'b', 'c']);
    const k = randomChoice(ECHELLE_K_VALUES);

    let bonneReponse;
    let suffixe;
    let texte;

    if (sousRegime === 'a') {
        // Carte → réel : x cm × k = (x·k) m
        const x = randomChoice(ECHELLE_Q_VALUES);
        bonneReponse = x * k;
        suffixe = ' m';
        const ctxCarte = randomChoice(ECHELLE_CONTEXTES_CARTE);
        texte =
            `<p>Sur une carte à l'échelle « 1 cm pour ${k} m », la distance entre deux ${ctxCarte.noun} est mesurée à ${x} cm.</p>`
            + `<p>Quelle est la distance réelle entre ces deux ${ctxCarte.noun} ?</p>`;
    } else if (sousRegime === 'b') {
        // Réel → carte : y m / k = (y/k) cm, avec y = q × k pour garantir entier
        const q = randomChoice(ECHELLE_Q_VALUES);
        const y = q * k;
        bonneReponse = q;
        suffixe = ' cm';
        const ctxCarte = randomChoice(ECHELLE_CONTEXTES_CARTE);
        texte =
            `<p>Deux ${ctxCarte.noun} sont distants de ${y} m.</p>`
            + `<p>Sur une carte à l'échelle « 1 cm pour ${k} m », à quelle distance seront-ils représentés sur la carte ?</p>`;
    } else {
        // Laisse → carte : L m / k = (L/k) cm
        const ctx = randomChoice(CONTEXTES_ANIMAL);
        const q = randomChoice([1, 2, 3, 4, 5, 6]); // rayons cm modérés sur carte
        const L = q * k;
        bonneReponse = q;
        suffixe = ' cm';
        const feminin = ctx.animal === 'chèvre';
        // « son » et non `ctx.possessif` : le possessif s'accorde au SUPPORT, et
        // les quatre supports du pool sont masculins. L'ancien code suivait
        // l'animal, d'où « la chèvre attachée à sa arbre » — 24,5 % du régime.
        texte =
            `<p>${feminin ? 'Une' : 'Un'} ${ctx.animal} est attaché${feminin ? 'e' : ''} à son ${ctx.support} par une laisse de <b>${L} m</b>.</p>`
            + `<p>Sur une carte à l'échelle « 1 cm pour ${k} m », quelle sera la longueur de la laisse représentée sur la carte (en cm) ?</p>`;
    }

    const payload = makeSaisie(String(bonneReponse), texte, {
        ...opts(L_ECHELLE),
        suffixe,
    });
    payload._meta.generatorKey = 'echelle_conversion_saisie';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    zone_atteignable_qcm_brouteur:  genZoneAtteignableQcmBrouteur,
    zone_figure_vf_brouteur:        genZoneFigureVfBrouteur,
    distance_max_saisie_brouteur:   genDistanceMaxSaisieBrouteur,
    tresor_deux_cercles_programme:  genTresorDeuxCerclesProgramme,
    echelle_conversion_saisie:      genEchelleSaisieConversion,
};

export const CATEGORIES = [
    { id: 'zone_atteignable_qcm',  label: L_ZONE_QCM,     formatsList: [{ key: 'zone_atteignable_qcm_brouteur',  label: 'QCM' }] },
    { id: 'zone_figure_vf',        label: L_ZONE_FIGURE,  formatsList: [{ key: 'zone_figure_vf_brouteur',        label: 'Vrai/Faux' }] },
    { id: 'distance_max_saisie',   label: L_DISTANCE_MAX, formatsList: [{ key: 'distance_max_saisie_brouteur',   label: 'Saisie' }] },
    { id: 'tresor_deux_cercles',   label: L_TRESOR,       formatsList: [{ key: 'tresor_deux_cercles_programme', label: 'Construction' }] },
    { id: 'echelle_conversion',    label: L_ECHELLE,      formatsList: [{ key: 'echelle_conversion_saisie',      label: 'Saisie' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    genZoneAtteignableQcmBrouteur,
    genZoneFigureVfBrouteur,
    genDistanceMaxSaisieBrouteur,
    genTresorDeuxCerclesProgramme,
    genEchelleSaisieConversion,
};
